import React, { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faUnlock, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { DateInput, FieldGroup } from "../../components";
import { CardType } from "./CardType";
import { PaymentType } from "./PaymentType";
import { RemovePaymentModal } from "./RemovePaymentModal";
import { currencyOptions, getBaseCurrency } from "../../core/currency";
import SendPaymentLinkModal from "../../order/components/ItemProduct/SendPaymentLinkModal";
import { Button } from "../../components/ui";

import "./payment-row.scss";

const STRIPE_LINK = "stripe_link";
const STRIPE_TERMINAL = "stripe_terminal";
const CASH = "cash";
const USDT = "usdt";

// How long to poll the backend for the in-person charge result before giving
// up and showing a timeout (the customer never tapped / reader went away).
const POS_POLL_INTERVAL_MS = 2000;
const POS_POLL_TIMEOUT_MS = 120000;

/**
 * A payment / deposit row. Supports six payment methods:
 *   - offline         → bank transfer (rep enters bank_account_id, fee, etc.)
 *   - card            → card payment, manually recorded by the rep
 *   - cash / usdt     → recorded by the rep exactly like offline (bank
 *                       account = the Xero account the money settles into)
 *   - stripe_link     → rep generates a Stripe payment link from this row;
 *                       customer pays via WhatsApp; webhook auto-fills
 *                       payment_status / card_type / fee / date.
 *   - stripe_terminal → POS: rep charges a physical Stripe Terminal reader;
 *                       customer taps/inserts; webhook auto-fills status/date.
 *
 * For stripe_link rows, Stripe is the source of truth for payment_status —
 * the lock toggle is hidden, and the Generate / Revoke / Regenerate button is
 * shown based on the link status. card_type / fee / date remain editable after
 * payment so the rep can override Stripe's values if needed, and the
 * bank-account field is editable at every stage (it is where Xero posts the
 * money, not part of taking it).
 *
 * Layout is split into two explicit lines so the fields don't compete for
 * horizontal space on typical viewports:
 *   Line 1 (left-group): currency · amount · date    (status pill trailing)
 *   Line 2 (left-group): method · card type · fee · bank-or-generate  (lock trailing)
 *   Below: optional hint ("Save the order first…") flush with line start.
 */
export function PaymentRow({
  idx,
  paymentStatus,
  setPaymentStatus,
  amount,
  setAmount,
  currency,
  setCurrency,
  date,
  setDate,
  method,
  setMethod,
  cardType,
  setCardType,
  fee,
  setFee,
  showErrors,
  bankAccountId,
  setBankAccountId,
  // Props for the Stripe Link flow — populated by the caller when this row
  // represents a per-listing deposit. Optional otherwise.
  order,
  orderItemId,
  paymentId,
  paymentLinkId,
  paymentLinkStatus,
  onLinkGenerated,
  onLinkRevoked,
  // POS (Stripe Terminal) flow — writes row fields back on charge/complete,
  // same shape as onLinkGenerated. Optional; only used for stripe_terminal rows.
  onPosUpdate,
  // "Remove payment" support. `canRemovePayment` reflects the current admin's
  // `remove_payment` capability (resolved one level up in Payments.jsx);
  // `onRemove` performs the DELETE + local-state cleanup and returns a promise
  // (resolve = removed, reject = show the server message in the modal). Both
  // are only passed for real, saved payment rows.
  canRemovePayment,
  onRemove,
  // Restricts which methods the PaymentType dropdown offers. Pass
  // MANUAL_PAYMENT_METHODS from Commissions (paid OUT to staff), EXPERT FEES
  // and the /sell flow (money TO seller post-acquisition) — Stripe Link / POS
  // don't apply there. Defaults to every method when undefined.
  allowedMethods,
  // Multi-org Xero: which connected org's bank accounts to load. Optional —
  // when undefined the backend returns the default org's accounts (legacy).
  xeroConnectionId,
  // Replaces the bank-account dropdown with a caller-supplied control, for rows
  // whose money does not settle into a Xero bank account. EXPERT FEES passes
  // its ExpertAccountSelect here. Unlike the bank dropdown this renders even
  // before an amount is typed — it is the control that says what the row is,
  // and the caller may want it picked first.
  accountField,
  // No Xero bank account applies — the document this money lands on is our
  // own PDF (no Xero on the plan). The dropdown is not rendered, its list is
  // not fetched, and nothing is asked for.
  hideBankAccount = false,
  // Hides the FX / card fee input. EXPERT FEES has no transaction fee: what
  // the store owes an expert is the agreed amount, full stop.
  hideFee = false,
}) {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);

  // POS (Stripe Terminal) local state. posPhase drives the button/label:
  //   idle → processing (waiting for the customer to tap) → done | failed.
  // "done" mirrors a completed payment_status; "failed" lets the rep retry.
  const isPos = method === STRIPE_TERMINAL;
  const [readers, setReaders] = useState([]);
  const [readerId, setReaderId] = useState("");
  const [posPhase, setPosPhase] = useState(
    paymentStatus === "completed" ? "done" : "idle"
  );
  const [posError, setPosError] = useState("");

  const isStripeLink = method === STRIPE_LINK;
  const linkIsOpen = paymentLinkStatus === "pending";
  const linkIsPaid = paymentLinkStatus === "paid";
  const linkIsClosedNotPaid =
    paymentLinkStatus === "expired" || paymentLinkStatus === "revoked";

  // POS: amount + currency lock once a charge is in flight or done, so the
  // collected amount can't drift. Bank account / card fee / card type stay
  // editable even after payment (so the rep can record where funds land and
  // correct Stripe's brand/fee) — and date stays editable after completion.
  const posLocked = isPos && (posPhase === "processing" || posPhase === "done");

  // For Stripe Link rows: Stripe drives payment_status, lock is hidden,
  // amount is locked once a link exists (pending or paid). card_type / fee /
  // date stay editable after payment so the rep can correct Stripe's values.
  const fieldsLocked = isStripeLink
    ? linkIsOpen || linkIsPaid
    : isPos
    ? posLocked
    : paymentStatus === "completed";

  const statusClass = (() => {
    if (isStripeLink) {
      if (linkIsPaid)            return "green";
      if (linkIsOpen)            return "blue";
      if (linkIsClosedNotPaid)   return "red";
      return "blue";
    }
    if (isPos) {
      if (posPhase === "done")       return "green";
      if (posPhase === "failed")     return "red";
      return "blue";
    }
    return paymentStatus === "completed" ? "green" : "blue";
  })();

  const statusName = (() => {
    if (isStripeLink) {
      if (linkIsPaid)            return "Paid";
      if (linkIsOpen)            return "Awaiting Payment";
      if (paymentLinkStatus === "expired") return "Link Expired";
      if (paymentLinkStatus === "revoked") return "Link Revoked";
      return "Not Generated";
    }
    if (isPos) {
      if (posPhase === "done")       return "Paid";
      if (posPhase === "processing") return "Waiting for card…";
      if (posPhase === "failed")     return "Payment Failed";
      return "Not Charged";
    }
    return paymentStatus === "completed" ? "Paid" : "Pending Confirmation";
  })();

  // Cash / USDT: a plain "Fee" (handling or network fee), not a card fee.
  const isCashOrUsdt = method === CASH || method === USDT;
  const feePlaceholder =
    (isPos || isCashOrUsdt
      ? "Fee: "
      : method !== "offline"
      ? "Card Fee: "
      : "FX Fee: ") + (fee ? "-" : "");

  const fetchAccountData = async () => {
    try {
      const response = await axios.get(`/api/xero/bank-accounts`, {
        params: xeroConnectionId ? { xero_connection_id: xeroConnectionId } : {},
      });
      if (response?.data) setBankAccounts(response.data);
    } catch {
      // Xero not connected or API error — keep default single-entry list
    }
  };

  // Refetch when the selected Xero org changes — accounts are per organisation.
  useEffect(() => {
    if (hideBankAccount) return;
    fetchAccountData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xeroConnectionId, hideBankAccount]);

  // STRIPE LINK is generatable on both deposit rows (which have an
  // orderItemId after save) and the TOTAL REMAINING PAID row (no
  // orderItemId — link is at order level, no Deposit is created).
  // Gate purely on the order existing; the modal only sends order_item_id
  // when set, and PaymentLinkService::generate accepts a null orderItemId.
  const canGenerate =
    isStripeLink &&
    !!order?.id &&
    !!amount &&
    !linkIsOpen &&
    !linkIsPaid;

  // Method-change guard: switching away from STRIPE LINK while a link is
  // open would orphan the Stripe PaymentIntent. Block the switch and offer
  // to revoke first.
  const handleMethodChange = (newMethod) => {
    if (isStripeLink && linkIsOpen) {
      const ok = window.confirm(
        "An open Stripe link exists for this row. Revoke it before switching method?"
      );
      if (!ok) return;
      handleRevoke(() => setMethod(newMethod));
      return;
    }
    if (isStripeLink && linkIsPaid) {
      window.alert(
        "This row's Stripe link has already been paid — method cannot be changed."
      );
      return;
    }
    if (isPos && posPhase === "processing") {
      window.alert(
        "A charge is in progress on the reader. Cancel it before switching method."
      );
      return;
    }
    setMethod(newMethod);

    // USDT is collected in USDT — default the row's currency to match (the
    // rep can still change it). A POS reader can't take USDT, so leaving
    // USDT for POS falls back to the store base currency.
    if (newMethod === USDT && currency !== USDT) {
      setCurrency?.(USDT);
    } else if (newMethod === STRIPE_TERMINAL && currency === USDT) {
      setCurrency?.(getBaseCurrency());
    }

    // Cash / USDT still settle into a Xero account (a Bank-type "Cash" or
    // "USDT wallet" account in that org). Pre-pick one whose name says so
    // when the rep hasn't chosen an account yet — editable like any other.
    if ((newMethod === CASH || newMethod === USDT) && !bankAccountId) {
      const pattern = newMethod === CASH ? /\bcash\b/i : /usdt|tether|crypto/i;
      const match = bankAccounts.find((a) => pattern.test(a?.name || ""));
      if (match) setBankAccountId?.(match.id);
    }
  };

  const handleRevoke = async (afterFn) => {
    if (!paymentLinkId || revoking) return;
    setRevoking(true);
    try {
      await axios.post(`/api/admin/payment-links/${paymentLinkId}/revoke`);
      onLinkRevoked?.();
      afterFn?.();
    } catch (e) {
      window.alert(
        e?.response?.data?.message || "Failed to revoke the link. Try again."
      );
    } finally {
      setRevoking(false);
    }
  };

  const openLinkModal = () => {
    if (!order?.id) {
      window.alert(
        "Please save the order first so the payment link can attach to it."
      );
      return;
    }
    setLinkModalOpen(true);
  };

  const handleLinkGenerated = (linkResponse) => {
    // Don't close the modal — let the rep see the success view with the
    // copyable URL, then close it themselves via the Done button. The
    // modal calls onClose (= setLinkModalOpen(false)) at that point.
    //
    // Don't echo the amount back either. linkResponse.amount comes back
    // in pence (e.g. 12300 for £123) but the row state stores major units
    // (123), so writing it back without dividing would multiply by 100.
    // The row amount didn't change anyway — only the link metadata did.
    onLinkGenerated?.({
      payment_id: linkResponse?.payment_id ?? null,
      payment_link_id: linkResponse?.id ?? null,
      payment_link_status: linkResponse?.status ?? "pending",
      payment_status: "created",
      method: STRIPE_LINK,
    });
  };

  // ── POS (Stripe Terminal) ─────────────────────────────────────────────────
  // Load registered readers for the picker once the row is a POS row.
  useEffect(() => {
    if (!isPos) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`/api/admin/terminal/readers`);
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setReaders(list);
        // Default to the first online reader (or any) when none chosen yet.
        setReaderId(
          (cur) =>
            cur ||
            list.find((r) => r.status === "online")?.id ||
            list[0]?.id ||
            ""
        );
      } catch {
        if (!cancelled) setReaders([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPos, order?.id]);

  const canCharge =
    isPos &&
    !!order?.id &&
    !!amount &&
    !!readerId &&
    posPhase !== "processing" &&
    posPhase !== "done";

  // Soft caution only — Stripe's reader status can lag, so we warn rather than
  // block. If it really is off/offline the charge fails fast (the backend now
  // handles terminal.reader.action_failed) with a clear message.
  const selectedReader = readers.find((r) => r.id === readerId);
  const selectedReaderOffline =
    !!selectedReader && selectedReader.status !== "online";

  // Poll the payment's status until the webhook resolves it (paid/failed) or
  // we time out waiting for the customer to tap.
  const pollPosStatus = (pid) => {
    if (!pid) return;
    const started = Date.now();
    const tick = async () => {
      try {
        const { data } = await axios.get(
          `/api/admin/payments/${pid}/terminal-status`
        );
        if (data?.status === "completed") {
          setPosPhase("done");
          onPosUpdate?.({
            payment_id: pid,
            method: STRIPE_TERMINAL,
            payment_status: "completed",
            ...(data.date ? { date: data.date } : {}),
            ...(data.card_type ? { card_type: data.card_type } : {}),
          });
          return;
        }
        if (data?.status === "failed" || data?.status === "cancelled") {
          setPosPhase("failed");
          setPosError("Payment failed or was cancelled — you can retry.");
          return;
        }
      } catch {
        // transient network error — keep polling until the timeout below
      }
      if (Date.now() - started > POS_POLL_TIMEOUT_MS) {
        setPosPhase("failed");
        setPosError("Timed out waiting for the card — you can retry.");
        return;
      }
      setTimeout(tick, POS_POLL_INTERVAL_MS);
    };
    setTimeout(tick, POS_POLL_INTERVAL_MS);
  };

  const handleCharge = async () => {
    if (!order?.id) {
      window.alert(
        "Please save the order first so the payment can attach to it."
      );
      return;
    }
    if (!readerId) {
      window.alert("Select a reader first.");
      return;
    }
    setPosError("");
    setPosPhase("processing");
    try {
      const { data } = await axios.post(`/api/admin/terminal/charge`, {
        order_id: order.id,
        reader_id: readerId,
        amount: Math.round(amount * 100),
        // Rows are seeded with the store-base default; this fallback only
        // covers a legacy row with no currency at all — use the base, not GBP.
        currency: (currency || getBaseCurrency()).toUpperCase(),
        ...(orderItemId ? { order_item_id: orderItemId } : {}),
        ...(paymentId ? { payment_id: paymentId } : {}),
        ...(bankAccountId ? { bank_account_id: bankAccountId } : {}),
      });
      // Persist the draft payment id so a retry reuses the same intent and
      // the row rehydrates correctly on reload.
      onPosUpdate?.({
        payment_id: data?.payment_id ?? paymentId ?? null,
        method: STRIPE_TERMINAL,
        payment_status: "created",
      });
      pollPosStatus(data?.payment_id);
    } catch (e) {
      setPosPhase("failed");
      setPosError(
        e?.response?.data?.message ||
          "The reader could not take the payment. Check it is online and retry."
      );
    }
  };

  const handlePosCancel = async () => {
    try {
      await axios.post(`/api/admin/terminal/cancel`, {
        reader_id: readerId,
        ...(paymentId ? { payment_id: paymentId } : {}),
      });
    } catch {
      // reader may already be idle — fall through to the failed state
    }
    setPosPhase("failed");
    setPosError("Charge cancelled — you can retry.");
  };

  return (
    <div className={`payment-row${isPos ? " payment-row--pos" : ""}`}>
      {/* Line 1 — currency + amount sit flush-left and never shift. Date
          appears to their right once the amount is entered. The status
          pill goes to the trailing edge of the line (justify-content
          space-between on the .payment-row__line) so its presence/absence
          doesn't displace the input fields. */}
      <div className="payment-row__line payment-row__line--top">
        <div className="payment-row__left-group">
          <div className="currency">
            <FieldGroup
              name="currency"
              type="select"
              // USDT (crypto) can't be collected on a card reader — hide it
              // for POS rows so the rep can't pick an uncollectable currency.
              options={currencyOptions().filter(
                (c) => !isPos || c.id !== "usdt"
              )}
              placeholder=""
              value={currency}
              onChange={setCurrency}
              noFormik
              disabled={fieldsLocked}
            />
          </div>

          <div className="amount">
            <FieldGroup
              type="numberFormat"
              noFormik
              value={amount}
              onChange={setAmount}
              readOnly={fieldsLocked}
            />
          </div>

          {!!amount && (
            <div className="deposit-date">
              <DateInput
                name={`deposit_date_${idx}`}
                value={date}
                onChange={setDate}
                maxAllowedDate={new Date()}
                className={
                  showErrors && amount > 0 && !date && !isStripeLink && !isPos
                    ? "error-border"
                    : ""
                }
                // Calendar popper renders into a portal — request-v2.scss
                // paints it dark via the rc-dark-calendar class.
                popperClassName="rc-dark-calendar"
                // stripe_link: locked while pending, editable after payment.
                // POS: locked only while charging; editable after completion
                // so the rep can override Stripe's date.
                readOnly={
                  isStripeLink
                    ? linkIsOpen
                    : isPos
                    ? posPhase === "processing"
                    : fieldsLocked
                }
                autocomplete="off"
                dateFormat="yyyy-MM-dd"
              />
            </div>
          )}

          {/* Bank-account selector — moved up next to the date so Line 2 has
              room for the method-specific controls (reader + Charge, etc.).
              Required on offline (transfer); optional on stripe_link / POS.
              A caller-supplied `accountField` takes its place entirely (EXPERT
              FEES), because that row never settles into a Xero account. */}
          {accountField ? (
            <div className="bank-destination">{accountField}</div>
          ) : (
            !!amount && !hideBankAccount && (
            <div className="bank-destination">
              <FieldGroup
                name="bank_account_type"
                type="select"
                className={
                  showErrors &&
                  amount > 0 &&
                  !bankAccountId &&
                  !isStripeLink &&
                  !isPos
                    ? "error-border"
                    : ""
                }
                options={bankAccounts}
                placeholder={
                  isStripeLink || isPos ? "Bank account" : "Select bank account"
                }
                value={bankAccountId}
                onChange={(selected) => {
                  setBankAccountId(selected);
                }}
                noFormik
                // ALWAYS editable on Stripe Link + POS rows, at every stage of
                // the link's life. This field is bookkeeping only — it names
                // the Xero account the money lands in and touches neither
                // Stripe nor the amount — so there is no reason to lock it, and
                // locking it while a link was open left the rep stuck: Go To
                // Invoice needs a real account, and the only way to reach the
                // field was to revoke the customer's live link. Posting is
                // gated on the payment being COMPLETED, backend-side
                // (XeroService::settlementAccountId), so setting it early
                // cannot record money we have not received.
                //
                // Rep-recorded rows (transfer / card) keep the completed lock,
                // because those DO have the padlock toggle to reopen them.
                disabled={isStripeLink || isPos ? false : fieldsLocked}
              />
            </div>
            )
          )}
        </div>

        {/* Remove payment — sits immediately to the LEFT of the status pill.
            Only for a real saved + completed payment ("Paid"), and only when
            the admin holds the remove_payment capability. Never shown on
            unpaid / draft / pending (incl. unpaid Stripe-link) rows. This only
            updates the order + Xero invoice; the modal makes the rep confirm
            the money was already refunded out-of-band before firing. */}
        {canRemovePayment &&
          typeof onRemove === "function" &&
          !!paymentId &&
          statusName === "Paid" && (
            <Button
              variant="danger"
              size="sm"
              className="remove-payment-btn"
              data-btn="payment-delete"
              onClick={() => setRemoveModalOpen(true)}
            >
              Remove payment
            </Button>
          )}

        {(!!amount || isStripeLink) && (
          <div className="payment-status">
            <div className={`order-status ${statusClass}`}>{statusName}</div>
          </div>
        )}
      </div>

      {/* Line 2 — method picker + method-specific fields all clustered
          flush-left in the left-group; the lock icon floats to the
          trailing edge so its visibility doesn't shift the inputs. */}
      {!!amount && (
        <div className="payment-row__line payment-row__line--bottom">
          <div className="payment-row__left-group">
            <PaymentType
              value={method}
              onChange={handleMethodChange}
              // Lock the method picker once a stripe_link is open or paid, or
              // while a POS charge is processing / done — the guards above
              // protect the intent, but disabling is clearer UX.
              readOnly={
                (isStripeLink && (linkIsOpen || linkIsPaid)) ||
                (isPos && (posPhase === "processing" || posPhase === "done"))
              }
              allowedMethods={allowedMethods}
            />

            {/* Only rendered when there IS a card brand to pick — an empty
                box here left a gap between the method dropdown and the fee on
                Transfer / Cash / USDT rows. */}
            {(method === "card" ||
              method === "stripe_card" ||
              isStripeLink ||
              isPos) && (
              <div className="card-type-container">
                <CardType
                  value={cardType}
                  onChange={setCardType}
                  // stripe_link: empty while pending, auto-fills on webhook,
                  // editable after. POS: auto-fills the brand on success,
                  // editable.
                  readOnly={isStripeLink && linkIsOpen}
                />
              </div>
            )}

            {!hideFee && (
            <div className="fee-amount">
              <FieldGroup
                type="numberFormat"
                name={`deposit_${idx}__fee`}
                persistLabel
                inputStyle={{
                  paddingLeft: isPos
                    ? "34px"
                    : isCashOrUsdt
                    ? "50px"
                    : (method && method.includes("card")) || isStripeLink
                    ? "100px"
                    : "80px",
                }}
                placeholder={feePlaceholder}
                noFormik
                value={fee}
                onChange={setFee}
                // stripe_link: editable except while pending. POS: always
                // editable (per requirement) so the rep can record/correct the
                // card fee even after the payment completes.
                readOnly={
                  isStripeLink ? linkIsOpen : isPos ? false : fieldsLocked
                }
              />
            </div>
            )}

            {isStripeLink && (
              <div className="stripe-link-actions">
                {canGenerate && (
                  <Button
                    variant="primary"
                    size="sm"
                    data-btn="payment-create-link"
                    onClick={openLinkModal}
                  >
                    {linkIsClosedNotPaid ? "Re-generate link" : "Generate link"}
                  </Button>
                )}
                {linkIsOpen && (
                  <Button
                    variant="danger"
                    size="sm"
                    data-btn="payment-cancel-link"
                    onClick={() => handleRevoke()}
                    loading={revoking}
                  >
                    Revoke
                  </Button>
                )}
              </div>
            )}

            {isPos && (
              <div className="pos-actions">
                {/* Reader picker — populated from registered readers scoped to
                    the configured Location. Locked while a charge is running. */}
                <div className="pos-reader">
                  <FieldGroup
                    name={`pos_reader_${idx ?? "trp"}`}
                    type="select"
                    options={readers.map((r) => ({
                      id: r.id,
                      name:
                        (r.label || r.id) +
                        (r.status === "online" ? "" : " (offline)"),
                    }))}
                    placeholder={
                      readers.length ? "Select reader" : "No readers found"
                    }
                    value={readerId}
                    onChange={setReaderId}
                    noFormik
                    disabled={posPhase === "processing"}
                  />
                </div>

                {posPhase !== "processing" && posPhase !== "done" && (
                  <Button
                    variant="primary"
                    size="sm"
                    data-btn="payment-create-charge"
                    onClick={handleCharge}
                    disabled={!canCharge}
                  >
                    {posPhase === "failed" ? "Retry" : "Charge"}
                  </Button>
                )}

                {posPhase === "processing" && (
                  <Button
                    variant="danger"
                    size="sm"
                    data-btn="payment-cancel-charge"
                    onClick={handlePosCancel}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            )}

          </div>

          {/* Lock toggle: hidden for stripe_link and POS rows because Stripe
              drives payment_status — admin cannot fake completion. */}
          {!isStripeLink && !isPos && (
            <div className="payment-lock">
              <div className="locker">
                {paymentStatus === "completed" ? (
                  <FontAwesomeIcon
                    icon={faLock}
                    size="2xl"
                    color="green"
                    onClick={() => setPaymentStatus("created")}
                    className="cursor-pointer"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faUnlock}
                    size="2xl"
                    color="red"
                    onClick={() => setPaymentStatus("completed")}
                    className="cursor-pointer"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Full-width hint on its own line — only shown when stripe_link is
          selected and the order hasn't been saved yet. */}
      {isStripeLink && !order?.id && (
        <div className="payment-row__hint">
          Save the order first to enable Stripe link generation.
        </div>
      )}

      {/* POS: while the charge is live, a prominent cue tells the rep to look
          at the machine. Replaces the need to guess what "Waiting for card…"
          means. */}
      {isPos && posPhase === "processing" && (
        <div className="payment-row__pos-waiting">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>Follow the prompts on the reader…</span>
        </div>
      )}

      {/* POS: prompt to save the order first, then surface any charge error. */}
      {isPos && !order?.id && (
        <div className="payment-row__hint">
          Save the order first to charge a reader.
        </div>
      )}

      {/* POS: warn (don't block) when the chosen reader looks offline. */}
      {isPos &&
        !!order?.id &&
        selectedReaderOffline &&
        posPhase !== "processing" &&
        posPhase !== "done" && (
          <div className="payment-row__hint payment-row__hint--error">
            This reader is offline — switch it on and check its internet, then
            charge (it will fail fast if it can’t be reached).
          </div>
        )}
      {isPos && posPhase === "failed" && !!posError && (
        <div className="payment-row__hint payment-row__hint--error">
          {posError}
        </div>
      )}

      {linkModalOpen && (
        <SendPaymentLinkModal
          open={linkModalOpen}
          onClose={() => setLinkModalOpen(false)}
          order={order}
          mode="inline-row"
          inlineAmount={amount * 100}
          inlineCurrency={(currency || getBaseCurrency()).toUpperCase()}
          inlineOrderItemId={orderItemId}
          inlinePaymentId={paymentId}
          // Carry an account picked BEFORE the link was generated, so it
          // survives: a pre-Generate row stores the __stripe_link__ sentinel in
          // bank_account_id (it is the only signal that hydrates the row as
          // STRIPE LINK), so Save alone cannot persist the rep's choice —
          // PaymentLinkService::generate can, via this field.
          inlineBankAccountId={bankAccountId}
          onGenerated={handleLinkGenerated}
        />
      )}

      {removeModalOpen && (
        <RemovePaymentModal
          open={removeModalOpen}
          onClose={() => setRemoveModalOpen(false)}
          // onRemove (from Payments.jsx) does the DELETE + refund confirmation
          // and refreshes the order/PX summary on success; it rejects with the
          // server error so the modal can surface the message (e.g. 502 Xero).
          onConfirm={() => onRemove(paymentId)}
        />
      )}
    </div>
  );
}
